import React from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { RichDescriptionBlock, RichDescriptionPayload } from 'src/entities/product';

const Block: React.FunctionComponent<{ block: RichDescriptionBlock }> = ({ block }) => {
  switch (block.type) {
    case 'heading': {
      const level = typeof block.level === 'number' ? block.level : 3;
      const variant =
        level <= 2 ? 'h6' : level === 3 ? 'subtitle1' : 'subtitle2';
      return (
        <Typography variant={variant} sx={{ mt: 1.5, mb: 0.5 }}>
          {block.text}
        </Typography>
      );
    }
    case 'paragraph':
      return (
        <Typography paragraph sx={{ mb: 1 }}>
          {block.text}
        </Typography>
      );
    case 'label':
      return (
        <Typography variant="body2" sx={{ mb: 0.5 }}>
          <b>{block.label}:</b> {block.value}
        </Typography>
      );
    case 'list':
    case 'features': {
      const ordered = block.type === 'list' && block.ordered;
      return (
        <Box component={ordered ? 'ol' : 'ul'} sx={{ m: 0, mb: 1, pl: 3 }}>
          {(block.items || []).map((item, index) => (
            <li key={index}>{item}</li>
          ))}
        </Box>
      );
    }
    case 'callout':
      return (
        <Box
          sx={{
            borderLeft: '4px solid',
            borderColor: 'primary.main',
            bgcolor: 'action.hover',
            p: 1.5,
            mb: 1,
          }}
        >
          <Typography variant="body2">{block.text}</Typography>
        </Box>
      );
    case 'quote':
      return (
        <Box sx={{ mb: 1 }}>
          <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
            «{block.text}»
          </Typography>
          {block.source && (
            <Typography variant="caption" color="text.secondary">
              {block.source}
            </Typography>
          )}
        </Box>
      );
    case 'footnote':
      return (
        <Typography
          variant="caption"
          color="text.secondary"
          display="block"
          sx={{ mb: 1 }}
        >
          {block.marker ? `${block.marker} ` : ''}
          {block.text}
        </Typography>
      );
    case 'table': {
      const rows = block.rows || [];
      return (
        <Table size="small" sx={{ mb: 1 }}>
          {block.headers && block.headers.length > 0 && (
            <TableHead>
              <TableRow>
                {block.headers.map((header, index) => (
                  <TableCell key={index}>
                    <b>{header}</b>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
          )}
          <TableBody>
            {rows.map((row, index) => (
              <TableRow key={index}>
                {row.map((cell, cellIndex) => (
                  <TableCell key={cellIndex}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      );
    }
    default:
      return (
        <Box
          component="pre"
          sx={{ fontSize: 12, whiteSpace: 'pre-wrap', mb: 1 }}
        >
          {JSON.stringify(block, null, 2)}
        </Box>
      );
  }
};

export const RichDescription: React.FunctionComponent<{
  payload?: RichDescriptionPayload | null;
}> = ({ payload }) => {
  const blocks = payload?.blocks;
  if (!blocks || blocks.length === 0) return null;

  return (
    <Box>
      {blocks.map((block, index) => (
        <Block key={index} block={block} />
      ))}
    </Box>
  );
};
